import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {classes, style} from './doppe-viewer-auto-form.st.css';
import type {FormController} from '@wix/devzai-utils-react';
import {
    reactCreateMemo,
    useFormController,
    useEventCallback,
    flexColumn,
} from '@wix/devzai-utils-react';
import type {Dictionary, Nullable, OmitStrict, Promisable} from '@wix/devzai-utils-common';
import {arrayRemoveNullishValues} from '@wix/devzai-utils-common';
import type {DoppeViewerSettingControlCustom} from './doppe-viewer-setting-control-custom';
import {DoppeViewerAutoFormField} from '../doppe-viewer-auto-form-field/doppe-viewer-auto-form-field';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import type {DoppeFormRequiredMarkingMode} from '../../client-server-common';

/** A form component for viewer that handles validation and field rendering using render props. */
export const DoppeViewerAutoForm = reactCreateMemo(function DoppeViewerAutoForm<
    FORM_DATA extends Dictionary
>(
    props: DoppeViewerAutoForm.Props<FORM_DATA>
) {
    const {
        initialFormData,
        formFields: formFieldsProp,
        validationFunction,
        onSubmit,
        onFormDataChange,
        requiredFieldMarkingMode,
        className,
        children,
        ...htmlAttributes
    } = props;

    const viewerLocales = useDoppeViewerLocales();

    const formFields = useMemo(() => {
        return arrayRemoveNullishValues(formFieldsProp);
    }, [formFieldsProp]);

    const combinedValidationFunction = useCallback<FormController.FormDataValidationFunction<FORM_DATA>>(
        (formData, errorsReporter) => {
            // Run field-level validators
            for (const field of formFields) {
                if (field.validators) {
                    for (const validator of field.validators) {
                        errorsReporter.report(field.fieldName, validator(formData[field.fieldName]));
                    }
                }
            }

            // Run custom validation function
            validationFunction?.(formData, errorsReporter);
        },
        [formFields, validationFunction]
    );

    const formController = useFormController<FORM_DATA>(initialFormData, {
        validationFunction: combinedValidationFunction,
    });

    const createBlurHandler = useCallback(
        (fieldName: keyof FORM_DATA) => {
            return () => {
                formController.startReportingErrors(fieldName);
            };
        },
        [formController]
    );

    const handleSubmit = useEventCallback(
        formController.createSubmitEventHandler(async (formData) => {
            await onSubmit(formData);
        })
    );

    const hasFormErrors = useMemo(() => {
        const errors = formController.formFieldsErrors;
        return Object.values(errors).some(error => error !== undefined && error.length > 0);
    }, [formController.formFieldsErrors]);

    const currentFormData = formController.getFormData();

    const onFormDataChangeRef = useRef(onFormDataChange);
    onFormDataChangeRef.current = onFormDataChange;
    
    useEffect(() => {
        onFormDataChangeRef.current?.(currentFormData);
    }, [currentFormData]);

    // Render form fields - not memoized to ensure values update on each render
    const renderedFormFields = formFields.map((field, index) => {
        const fieldName = field.fieldName;
        const fieldErrors = formController.formFieldsErrors[fieldName];
        const firstError = fieldErrors?.[0];

        return (
            <DoppeViewerAutoFormField
                key={`${String(fieldName)}-${index}`}
                label={field.title}
                description={field.description}
                control={field.control}
                value={formController.getValue(fieldName)}
                onValueChange={(value) => {
                    formController.setValue(fieldName, value);
                }}
                onBlur={createBlurHandler(fieldName)}
                validationError={firstError}
                mandatory={field.mandatory}
                disabled={field.disabled}
                formData={currentFormData}
                className={field.className}
                requiredFieldMarkingMode={requiredFieldMarkingMode}
            />
        );
    });

    const formError = hasFormErrors ? viewerLocales['formValidationError'] : undefined;

    return (
        <form
            {...htmlAttributes}
            className={style(classes.root, {}, className, flexColumn())}
            onSubmit={handleSubmit}
            noValidate={true}
        >
            {children({
                formFields: renderedFormFields,
                formError: formError,
                formData: formController.getFormData(),
                formController: formController,
                hasErrors: hasFormErrors,
            })}
        </form>
    );
});

export namespace DoppeViewerAutoForm {

    export type FormData = Dictionary;

    /**
     * Definition of a form field.
     */
    export type FormField<FORM_DATA extends FormData> = {
        [FIELD_NAME in keyof FORM_DATA & string]: {
            /**
             * The field name in the form data object.
             */
            fieldName: FIELD_NAME;

            /**
             * Label to display above the field.
             */
            title?: string;

            /**
             * Optional description text displayed below the label.
             */
            description?: string;

            /**
             * The custom control to render for this field.
             */
            control: DoppeViewerSettingControlCustom<FORM_DATA[FIELD_NAME]>;

            /**
             * Whether the field is mandatory (shows * indicator).
             */
            mandatory?: boolean;

            /**
             * Whether the field is disabled.
             */
            disabled?: boolean;

            /**
             * Field-level validators.
             */
            validators?: FormController.FormFieldValidator<FORM_DATA[FIELD_NAME]>[];

            /**
             * Optional CSS class name for the field.
             */
            className?: string;
        };
    }[keyof FORM_DATA & string];

    /**
     * Render props passed to the children function.
     */
    export interface RenderProps<FORM_DATA extends FormData> {
        /**
         * Rendered form field components.
         */
        formFields: React.ReactNode;

        /**
         * General form error message (shown when form has validation errors).
         */
        formError?: string;

        /**
         * Current form data.
         */
        formData: FORM_DATA;

        /**
         * The form controller instance.
         */
        formController: FormController<FORM_DATA>;

        /**
         * Whether the form has any validation errors.
         */
        hasErrors: boolean;
    }

    export interface Props<FORM_DATA extends FormData> extends OmitStrict<React.FormHTMLAttributes<HTMLFormElement>, 'children' | 'onSubmit'> {
        /**
         * Initial form data values.
         */
        initialFormData: FORM_DATA;

        /**
         * Array of form field definitions.
         */
        formFields: Nullable<FormField<FORM_DATA>>[];

        /**
         * Custom validation function.
         */
        validationFunction?: FormController.FormDataValidationFunction<FORM_DATA>;

        /**
         * Callback when form is submitted with valid data.
         */
        onSubmit: (formData: FORM_DATA) => Promisable<void>;

        /**
         * Callback when form data changes.
         * Called on every form data update (field value change).
         */
        onFormDataChange?: (formData: FORM_DATA) => void;

        /**
         * Render function for form content.
         */
        children: (renderProps: RenderProps<FORM_DATA>) => React.ReactNode;

        /**
         * How required fields should be marked in field labels.
         * Forwarded to each rendered {@link DoppeViewerAutoFormField}; defaults there to Asterisk.
         */
        requiredFieldMarkingMode?: DoppeFormRequiredMarkingMode;
    }
}
